import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@/config/Grid/Grid";
import Button from "@/components/Button/Button";
import IconButton from "@/components/IconButton/IconButton";
import { useMenuData } from "@/hooks/useMenuData";
import { formatGrosze, tipFeeGrosze } from "@/helpers/cartPricing";
import {
  useCartActions,
  useCartBlikCode,
  useCartLines,
  useCartPaymentMethod,
  useCartStatus,
  useCartSubtotal,
  useCartTipGrosze,
} from "@/store/useCartStore";
import { useTipFeeBps } from "@/store/useVenuesDataStore";
import { useSubmitOrder } from "@/hooks/useSubmitOrder";
import { orderPaths } from "@/routes/paths";
import { useGoBack } from "@/hooks/useGoBack";
import CartLineTile from "./components/CartLineTile";
import OrderActionBar from "./components/OrderActionBar";
import styles from "./Order.module.scss";

function CartPage() {
  const { restaurantId = "", tableId = "" } = useParams<{
    restaurantId: string;
    tableId: string;
  }>();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const navigate = useNavigate();
  const paths = orderPaths(restaurantId, tableId);
  const goBack = useGoBack(paths.root);

  const lines = useCartLines();
  const subtotal = useCartSubtotal();
  const tipGrosze = useCartTipGrosze();
  const status = useCartStatus();
  const paymentMethod = useCartPaymentMethod();
  const blikCode = useCartBlikCode();
  const { setQuantity, removeLine } = useCartActions();
  const { submit } = useSubmitOrder();

  // The checkout steps are child routes: this page stays mounted underneath
  // while the gradient drawer shows the step, so the bottom bar belongs here.
  const { pathname } = useLocation();
  const step = pathname.endsWith("/payment")
    ? "payment"
    : pathname.endsWith("/tip")
      ? "tip"
      : "cart";
  const tipFeeBps = useTipFeeBps();
  const total = subtotal + tipGrosze + tipFeeGrosze(tipGrosze, tipFeeBps);
  const isSubmitting = status === "submitting";
  // Blik needs the full six-digit code before there is anything to charge.
  const paymentReady =
    Boolean(paymentMethod) &&
    (paymentMethod !== "Blik" || blikCode.length === 6);

  // Check the cart against the menu: a dish pulled since it was added would be
  // rejected by the server, so surface it before checkout rather than at
  // payment. Read from the query cache, so this is the menu as of the last
  // fetch rather than a fresh one; the server stays the authority, and an
  // order that slips through is caught by the failure screen.
  const { dishes, loading } = useMenuData(restaurantId);
  const unavailableLineIds = useMemo(() => {
    if (loading || dishes.length === 0) return new Set<string>();
    const availableIds = new Set(dishes.map((dish) => dish.id));
    return new Set(
      lines
        .filter((line) => !availableIds.has(line.dishId))
        .map((line) => line.lineId),
    );
  }, [dishes, lines, loading]);

  const isEmpty = lines.length === 0;
  const canCheckout = !isEmpty && unavailableLineIds.size === 0;
  // A cart of nothing but rewards costs nothing, so there is no tip to leave
  // and no payment to take — it goes straight to the kitchen.
  const isFree = !isEmpty && total === 0;

  // Reaching a checkout step with nothing to buy — a back-navigation after
  // ordering, or a pasted URL — should fall back to the cart, not show a
  // payment drawer for 0.00 zł.
  useEffect(() => {
    if ((isEmpty || isFree) && step !== "cart") {
      navigate(paths.cart, { replace: true });
    }
  }, [isEmpty, isFree, step, navigate, paths.cart]);

  return (
    <>
      <Grid>
        <div className={`start-1 col-4 ${styles.orderWrapper}`}>
          <div className={styles.orderHeaderContainer}>
            <div className={styles.orderHeader}>
              <IconButton
                iconName="las la-angle-left icon-big"
                onClick={goBack}
                color="primary"
                variant="text"
              />
              <p className={`body-bold-large ${styles.orderTitle}`}>
                {t("order.cart.title")}
              </p>
              <IconButton
                iconName=""
                onClick={() => {}}
                color="primary"
                variant="text"
              />
            </div>
          </div>

          <div className={styles.orderContent}>
            {isEmpty ? (
              <>
                <p className={`body-medium ${styles.orderStateMessage}`}>
                  {t("order.cart.empty")}
                </p>
                <Button
                  onClick={() => navigate(paths.root)}
                  label={t("order.button.browseMenu")}
                  variant="outlined"
                  color="primary"
                  mobile={true}
                />
              </>
            ) : (
              <div className={styles.orderSection}>
                {lines.map((line) => (
                  <CartLineTile
                    key={line.lineId}
                    line={line}
                    language={language}
                    unavailable={unavailableLineIds.has(line.lineId)}
                    onQuantityChange={(quantity) =>
                      setQuantity(line.lineId, quantity)
                    }
                    onRemove={() => removeLine(line.lineId)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </Grid>

      {!isEmpty && step === "cart" ? (
        <OrderActionBar
          summaryLabel={t("order.cart.subtotal")}
          summaryValue={formatGrosze(subtotal)}
        >
          <Button
            onClick={isFree ? submit : () => navigate(paths.tip)}
            label={
              isFree
                ? isSubmitting
                  ? t("order.button.paying")
                  : t("order.button.placeOrder")
                : t("order.button.payAndOrder")
            }
            variant="contained"
            color="primary"
            maxWidth
            mobile={true}
            disabled={!canCheckout || (isFree && isSubmitting)}
          />
        </OrderActionBar>
      ) : null}

      {!isEmpty && step === "tip" ? (
        <OrderActionBar
          summaryLabel={t("order.tip.total")}
          summaryValue={formatGrosze(total)}
        >
          <Button
            onClick={() => navigate(paths.payment)}
            label={t("order.button.selectPaymentMethod")}
            variant="contained"
            color="primary"
            maxWidth
            mobile={true}
          />
        </OrderActionBar>
      ) : null}

      {!isEmpty && step === "payment" ? (
        <OrderActionBar>
          <Button
            onClick={submit}
            label={
              isSubmitting
                ? t("order.button.paying")
                : `${t("order.button.pay")} ${formatGrosze(total)}`
            }
            variant="contained"
            color="primary"
            maxWidth
            mobile={true}
            disabled={isSubmitting || !paymentReady || !canCheckout}
          />
        </OrderActionBar>
      ) : null}

      {/* The checkout steps are child routes that render the gradient drawer
          over this page; they have no markup of their own. */}
      <Outlet />
    </>
  );
}

export default CartPage;
